import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { InterpreterViewStore } from "../../store";
import { InterpreterView } from "../view/InterpreterView";
import { InterpreterUpdateStore } from "../../store";
import { InterpreterUpdate } from "../update/InterpreterUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type InterpreterModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const InterpreterModalContent: React.FC<InterpreterModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new InterpreterUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new InterpreterViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <InterpreterView
            interpreterViewStore={storeInfo.store as InterpreterViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <InterpreterUpdate
            interpreterUpdateStore={storeInfo.store as InterpreterUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
